"use client";

import { useAnalyticsCaptureReady } from "@analytics/lib/analytics-capture-ready-context";
import { useConsent } from "@shared/hooks/cookie-consent";
import { CONSENT_VERSION } from "@shared/lib/consent/constants";
import { useEffect } from "react";

export function buildMountOnceStorageKey(eventKey: string): string {
	return `${eventKey}:cmp-v${CONSENT_VERSION}`;
}

export function hasTrackedMountEvent(storageKey: string): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	return window.sessionStorage.getItem(storageKey) === "1";
}

export function markTrackedMountEvent(storageKey: string): void {
	if (typeof window === "undefined") {
		return;
	}
	window.sessionStorage.setItem(storageKey, "1");
}

export function useTrackMountOnceEvent(
	eventKey: string,
	onTrack: () => void,
): void {
	const captureReady = useAnalyticsCaptureReady();
	const { hasConsent, status } = useConsent();

	useEffect(() => {
		if (status === "unknown" || !hasConsent("analytics") || !captureReady) {
			return;
		}
		const storageKey = buildMountOnceStorageKey(eventKey);
		if (hasTrackedMountEvent(storageKey)) {
			return;
		}
		markTrackedMountEvent(storageKey);
		onTrack();
	}, [captureReady, eventKey, hasConsent, onTrack, status]);
}
